import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShoppingBag, Plus, ArrowRight, Lock, Bell, Check } from "lucide-react";
import { useCart } from "@/context/CartContext";
import Product360 from "@/components/Product360";

const DEFAULT_SIZES = ["S", "M", "L", "XL"];

function NotifyMeForm({ productId, productName, variant = "hero" }) {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email || submitting) return;
        setSubmitting(true);
        try {
            const r = await api.post("/waitlist", { email, product_id: productId });
            setDone(true);
            if (r.data?.already_subscribed) {
                toast.success("You're already on the list — we'll email you when it drops.");
            } else {
                toast.success(`On the list. We'll email you the second ${productName.split("·")[1]?.trim() || "it"} drops.`);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not join waitlist. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div
                data-testid={`waitlist-success-${productId}`}
                className={`${
                    variant === "hero" ? "mt-6 px-4 py-4" : "mt-3 px-3 py-3"
                } border border-white/25 bg-white/5 flex items-center gap-3`}
            >
                <Check size={16} className="text-white shrink-0" />
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white">
                    You're on the list
                </p>
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            data-testid={`waitlist-form-${productId}`}
            className={`${variant === "hero" ? "mt-6" : "mt-3"} flex border border-white/20 bg-black/40 focus-within:border-white/60 transition-colors`}
        >
            <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid={`waitlist-email-${productId}`}
                className={`flex-1 bg-transparent ${
                    variant === "hero" ? "px-4 py-3 text-sm" : "px-3 py-2.5 text-xs"
                } font-sans text-white placeholder:text-muted-foreground/60 outline-none`}
            />
            <button
                type="submit"
                disabled={submitting || !email}
                data-testid={`waitlist-submit-${productId}`}
                className={`bg-white text-black ${
                    variant === "hero" ? "px-5 py-3" : "px-3 py-2.5"
                } font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-white/85 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 shrink-0`}
            >
                <Bell size={12} />
                {submitting ? "..." : "Notify"}
            </button>
        </form>
    );
}

function ProductImage({ p, autoSpin = false }) {
    if (!p.image_url) {
        // No image yet — placeholder
        return (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                <Lock size={28} className="text-white/40 mb-4" />
                <p className="label-mono text-white/50">— Product preview</p>
                <p className="font-display text-2xl md:text-3xl uppercase tracking-tight mt-2">
                    Coming soon
                </p>
                {p.release_label && (
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-3">
                        {p.release_label}
                    </p>
                )}
            </div>
        );
    }
    if (p.static_image) {
        // Flat product photo — no 360° rotation.
        return (
            <div className="absolute inset-0 flex items-center justify-center p-6">
                <img
                    src={p.image_url}
                    alt={p.name}
                    className="max-w-full max-h-full object-contain"
                    draggable="false"
                />
            </div>
        );
    }
    return (
        <Product360
            imageUrl={p.image_url}
            backImageUrl={p.back_image_url || null}
            splitFront={p.split_front !== false}
            auto={autoSpin}
            duration={14}
            alt={p.name}
        />
    );
}

function SizeChips({ sizes, value, onChange, productId, disabled = false }) {
    return (
        <div className="flex items-center gap-1.5 flex-wrap">
            {sizes.map((s) => (
                <button
                    key={s}
                    onClick={() => !disabled && onChange(s)}
                    disabled={disabled}
                    data-testid={`size-${productId}-${s}`}
                    data-cursor-label={s}
                    className={`w-8 h-8 font-mono text-[10px] uppercase border transition-colors ${
                        disabled
                            ? "border-white/10 text-muted-foreground/40 cursor-not-allowed"
                            : value === s
                            ? "bg-white text-black border-white"
                            : "border-white/15 text-muted-foreground hover:border-white hover:text-white"
                    }`}
                >
                    {s}
                </button>
            ))}
        </div>
    );
}

function HeroProductCard({ p, index, total }) {
    const sizes = p.sizes && p.sizes.length > 0 ? p.sizes : DEFAULT_SIZES;
    const [size, setSize] = useState(sizes[1] || sizes[0] || "M");
    const { addItem, setOpen } = useCart();
    const isComing = !!p.coming_soon;

    const onAdd = () => {
        addItem(p, { size, quantity: 1 });
        toast.success(`Added · ${p.name} (${size})`);
        setOpen(true);
    };

    return (
        <motion.article
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center"
            data-testid={`product-card-${p.id}`}
            data-cursor="hover"
        >
            <div className="lg:col-span-7 relative product-photo border border-white/10 aspect-[4/5] lg:aspect-[5/6]">
                <ProductImage p={p} autoSpin={!isComing && !!p.image_url} />
                <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] bg-black/70 px-2 py-1 backdrop-blur-sm z-20">
                    Feat · 0{index + 1} / {total}
                </div>
                {!isComing ? (
                    <div className="absolute bottom-4 right-4 font-mono text-[10px] uppercase tracking-[0.3em] bg-white text-black px-2 py-1 z-20">
                        Just Dropped
                    </div>
                ) : (
                    <div className="absolute bottom-4 right-4 font-mono text-[10px] uppercase tracking-[0.3em] bg-signal text-white px-2 py-1 z-20">
                        Coming Soon
                    </div>
                )}
            </div>

            <div className="lg:col-span-5 lg:pl-8">
                <p className="label-mono mb-3">
                    {isComing ? "— Coming Soon" : "— Featured Piece"}
                </p>
                <h3 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                    {p.name}
                </h3>
                <p className="font-sans text-muted-foreground mt-6 text-base leading-relaxed max-w-md">
                    {p.description}
                </p>

                {isComing ? (
                    <div className="mt-10 border border-white/15 px-6 py-6 bg-black/40">
                        <p className="label-mono">Status</p>
                        <p className="font-display text-3xl md:text-4xl uppercase tracking-tight mt-2">
                            {p.release_label || "Coming Soon"}
                        </p>
                        <p className="font-sans text-sm text-muted-foreground mt-3">
                            Be first when it drops. We'll email you the moment it goes live.
                        </p>
                        <NotifyMeForm productId={p.id} productName={p.name} variant="hero" />
                    </div>
                ) : (
                    <>
                        <div className="mt-10 grid grid-cols-2 gap-6 border-t border-white/15 pt-6">
                            <div>
                                <p className="label-mono mb-2">Price</p>
                                <p className="font-display text-3xl" data-testid={`price-${p.id}`}>
                                    ${p.price.toFixed(2)}
                                </p>
                            </div>
                            <div>
                                <p className="label-mono mb-2">Category</p>
                                <p className="font-sans uppercase text-sm tracking-wide">{p.category}</p>
                            </div>
                        </div>

                        <div className="mt-8">
                            <p className="label-mono mb-3">Select Size</p>
                            <SizeChips
                                sizes={sizes}
                                value={size}
                                onChange={setSize}
                                productId={p.id}
                            />
                        </div>

                        <button
                            onClick={onAdd}
                            data-testid={`add-to-cart-${p.id}`}
                            data-cursor-label="Add ↗"
                            className="mt-8 w-full inline-flex items-center justify-between gap-2 bg-white text-black px-6 py-4 font-mono text-xs uppercase tracking-[0.3em] hover:bg-white/85 transition-colors group"
                        >
                            <span className="flex items-center gap-2">
                                <ShoppingBag size={14} />
                                Add to Bag
                            </span>
                            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </>
                )}
            </div>
        </motion.article>
    );
}

function CompactProductCard({ p, index }) {
    const sizes = p.sizes && p.sizes.length > 0 ? p.sizes : DEFAULT_SIZES;
    const [size, setSize] = useState(sizes[1] || sizes[0] || "M");
    const { addItem, setOpen } = useCart();
    const isComing = !!p.coming_soon;

    const onAdd = () => {
        addItem(p, { size, quantity: 1 });
        toast.success(`Added · ${p.name} (${size})`);
        setOpen(true);
    };

    return (
        <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.8, delay: index * 0.1 }}
            className="group relative flex flex-col"
            data-testid={`product-card-${p.id}`}
            data-cursor="hover"
        >
            <div className="relative aspect-[3/4] product-photo border border-white/10">
                <ProductImage p={p} />
                <div className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.25em] bg-black/70 px-2 py-1 backdrop-blur-sm z-20">
                    0{index + 2}
                </div>
                {isComing && (
                    <div className="absolute top-3 right-3 font-mono text-[10px] uppercase tracking-[0.25em] bg-signal text-white px-2 py-1 z-20">
                        Soon
                    </div>
                )}
                {!isComing && (
                    <button
                        onClick={onAdd}
                        data-testid={`add-to-cart-${p.id}`}
                        data-cursor-label="Add ↗"
                        aria-label="Add to bag"
                        className="absolute bottom-3 right-3 w-10 h-10 bg-white text-black inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all translate-y-2 group-hover:translate-y-0 z-20"
                    >
                        <Plus size={16} />
                    </button>
                )}
            </div>
            <div className="mt-4 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                    <h3 className="font-sans uppercase text-sm tracking-wide truncate">
                        {p.name}
                    </h3>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                        {p.category}
                    </p>
                </div>
                {isComing ? (
                    <p
                        className="font-mono text-[10px] uppercase tracking-[0.2em] text-signal shrink-0 text-right"
                        data-testid={`coming-soon-${p.id}`}
                    >
                        {p.release_label || "Coming Soon"}
                    </p>
                ) : (
                    <p className="font-display text-2xl shrink-0" data-testid={`price-${p.id}`}>
                        ${p.price.toFixed(0)}
                    </p>
                )}
            </div>
            {!isComing && (
                <div className="mt-3">
                    <SizeChips sizes={sizes} value={size} onChange={setSize} productId={p.id} />
                </div>
            )}
            {isComing && (
                <NotifyMeForm productId={p.id} productName={p.name} variant="compact" />
            )}
        </motion.article>
    );
}

export default function FeaturedDrops() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await api.get("/products", { params: { featured: true } });
                setProducts(res.data);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const [hero, ...rest] = products;

    return (
        <section
            id="shop"
            data-testid="featured-drops"
            className="relative bg-black py-24 lg:py-32 border-t border-white/10 overflow-hidden"
        >
            <p
                aria-hidden
                className="absolute -top-10 right-4 lg:right-12 font-display text-[14rem] lg:text-[22rem] leading-none text-outline opacity-[0.12] select-none pointer-events-none tabular-nums"
            >
                01
            </p>

            <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                    <div>
                        <p className="label-mono mb-4">— 01 / Shop The Drop</p>
                        <h2 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase tracking-tighter leading-[0.85]">
                            The Current
                            <br />
                            <span className="italic font-normal">collection.</span>
                        </h2>
                    </div>
                    <p className="font-sans text-base text-muted-foreground max-w-sm">
                        Each piece is conceived as an essential — designed to outlast trends and amplify your presence.
                    </p>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        <div className="lg:col-span-7 aspect-[5/6] bg-white/5 animate-pulse" />
                        <div className="lg:col-span-5 space-y-4">
                            <div className="h-8 w-2/3 bg-white/5 animate-pulse" />
                            <div className="h-4 w-full bg-white/5 animate-pulse" />
                            <div className="h-4 w-3/4 bg-white/5 animate-pulse" />
                        </div>
                    </div>
                ) : (
                    <>
                        {hero && <HeroProductCard p={hero} index={0} total={products.length} />}

                        {rest.length > 0 && (
                            <>
                                <div className="my-20 flex items-center gap-6">
                                    <div className="h-line flex-1" />
                                    <p className="label-mono">Continue · Browse all</p>
                                    <div className="h-line flex-1" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
                                    {rest.map((p, i) => (
                                        <CompactProductCard key={p.id} p={p} index={i} />
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </section>
    );
}
